import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { isFuture } from 'date-fns';
import PropTypes from 'prop-types';
import { MINUTE } from 'time-constants';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useWeatherAlertsCrawl } from '@hooks';

import { replaceAllInString } from '../../../helpers/StringUtils';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { TWCTextTicker } from '../../primitives/TWCTextTicker/TWCTextTicker';
import { LDL } from '../styles';

const testIDPath = lowerDisplayLineTestIDs.severeCrawl;
const crawlPreparationSpace = Theme.px(20);
const crawlingSpeed = 110;

const LDLAlertCrawlAPI = ({ testID, isSevere }) => {
  const data = useWeatherAlertsCrawl(isSevere);

  const shouldCrawl = data.alerts.length > 0 && isFuture(TimeUtils.dateFromEpoch(data.highestExpireTimeUTC));

  return <LDLAlertCrawlUI data={data} shouldCrawl={shouldCrawl} testID={testID} />;
};

LDLAlertCrawlAPI.propTypes = {
  testID: PropTypes.string,
  isSevere: PropTypes.bool,
};

LDLAlertCrawlAPI.defaultProps = {
  testID: null,
  isSevere: false,
};

const getCrawlText = ({ texts = [] } = {}) =>
  texts.map(({ description }) => replaceAllInString(description ?? '', '\n', ' ')).join(' ');

const LDLAlertCrawlUI = ({ data, testID, shouldCrawl, children }) => {
  const { alerts, highestCrawlLimitMinutes, highestCrawlMinutesOnOff, highestExpireTimeUTC } = data;
  const [alertIndex, setAlertIndex] = useState(0);
  const [bulletinWidth, setBulletinWidth] = useState(0);
  const [visible, setVisible] = useState(shouldCrawl);
  const crawlingAlertId = useRef(null);
  const crawlElapsedMinutes = useRef(0);
  const opacity = useRef(new Animated.Value(1)).current;

  const resetCrawl = () => {
    crawlElapsedMinutes.current = 0;
    Animated.timing(opacity).reset();
    setVisible(shouldCrawl);
    if (shouldCrawl) {
      crawlingAlertId.current = alerts[alertIndex]?.detailKey;
      runCrawl();
    }
  };

  // Reset crawling whenever shouldCrawl props is changed
  useEffect(() => {
    resetCrawl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCrawl]);

  // Reset crawling when the alerts data changes.
  useEffect(() => {
    if (alertIndex >= alerts.length) {
      setAlertIndex(0);
      return;
    }

    const newDisplayedAlert = alerts[alertIndex]?.detailKey;

    if (newDisplayedAlert != null && newDisplayedAlert !== crawlingAlertId.current) {
      resetCrawl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts, alertIndex]);

  const runCrawl = useCallback(
    (toValue = 0) => {
      Animated.timing(opacity, {
        delay: highestCrawlMinutesOnOff * MINUTE,
        duration: 50,
        toValue,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) {
          crawlElapsedMinutes.current += highestCrawlMinutesOnOff;
          setVisible(Boolean(toValue));
          // repeat toggling until the alert expires or we hit the crawlLimitMinutes value
          if (
            isFuture(TimeUtils.dateFromEpoch(highestExpireTimeUTC)) &&
            (!highestCrawlLimitMinutes || crawlElapsedMinutes.current < highestCrawlLimitMinutes)
          ) {
            runCrawl(1 - toValue);
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [highestCrawlMinutesOnOff, highestCrawlLimitMinutes],
  );

  if (alerts.length === 0 || !visible) {
    return <>{children}</>;
  }

  const bulletinLabel = alerts[alertIndex]?.eventDescription;
  const bulletinText = getCrawlText(alerts[alertIndex]);
  const stageWidth = LDL.contentWidth - bulletinWidth;

  if (!bulletinLabel || !bulletinText) {
    console.log(`got some bad alert data, index: ${alertIndex}`, alerts);
  }

  return bulletinLabel && bulletinText ? (
    <Animated.View style={[styles.crawlWrapper, { opacity }]} testID={testID}>
      <View
        style={styles.bulletinLabel}
        onLayout={({
          nativeEvent: {
            layout: { width },
          },
        }) => setBulletinWidth(width)}
      >
        <TWCText testID={testIDPath.severeCrawlLabel} variant={'mediumCondensed'}>
          {bulletinLabel}
        </TWCText>
      </View>
      <View style={styles.crawlLabel}>
        <TWCTextTicker
          loops
          animationDelay={10}
          scrollSpacer={stageWidth + crawlPreparationSpace}
          scrollSpeed={crawlingSpeed}
          style={[styles.labelText, { width: stageWidth }]}
          testID={testIDPath.severeCrawlText}
          variant={'mediumCondensed'}
          onAnimationEnd={() => setAlertIndex((alertIndex + 1) % alerts.length)}
        >
          {bulletinText}
        </TWCTextTicker>
      </View>
    </Animated.View>
  ) : null;
};

LDLAlertCrawlUI.propTypes = {
  data: PropTypes.object,
  testID: PropTypes.string,
  shouldCrawl: PropTypes.bool,
  children: PropTypes.node,
};

LDLAlertCrawlUI.defaultProps = {
  data: null,
  testID: null,
  shouldCrawl: false,
  children: null,
};

export const LDLAlertCrawl = {
  API: LDLAlertCrawlAPI,
  UI: LDLAlertCrawlUI,
};

const styles = StyleSheet.create({
  bulletinLabel: {
    alignItems: 'center',
    backgroundColor: Theme.severeAlertColor,
    height: '100%',
    justifyContent: 'center',
    paddingLeft: Theme.px(18),
    paddingRight: Theme.px(18),
  },
  crawlLabel: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
  },
  crawlWrapper: {
    flexDirection: 'row',
    height: LDL.height,
    left: LDL.timeLogoWidth + LDL.paddingLeft,
    overflow: 'hidden',
    position: 'absolute',
    width: LDL.contentWidth,
    zIndex: 1,
  },
  labelText: {
    height: LDL.height,
  },
});
